import React, { useEffect, useState, useCallback } from "react";
import { getPatientBookings } from "../Services/api";
import { RefreshCcw, CalendarCheck, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import Loader from './Loader';
import { useDarkMode } from "../hooks/useDarkMode";

export default function BookingsTab({ patientId, onSwitchToRegistration }) {
  const [bookings, setBookings] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tokenModal, setTokenModal] = useState(null);
  const [tick, setTick] = useState(0);
  const { t } = useTranslation();
  const isDark = useDarkMode();

  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const _storedPatient = (() => {
    try {
      const raw = localStorage.getItem("patient");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  })();

  const patientName =
    [
      _storedPatient?.salutation,
      _storedPatient?.name ||
        [_storedPatient?.firstName, _storedPatient?.middleName, _storedPatient?.lastName]
          .filter(Boolean)
          .join(" "),
    ]
      .filter(Boolean)
      .join(" ") || "";

  const patientIdentifier = _storedPatient?.patientId || _storedPatient?.id || "";

  const initials = (() => {
    const f = _storedPatient?.firstName?.[0] || "";
    const l = _storedPatient?.lastName?.[0] || "";
    return (f + l).toUpperCase() || "P";
  })();

  const gender = _storedPatient?.gender;
  const avatarBg =
    gender === "Male" ? "linear-gradient(135deg,#2563eb,#1d4ed8)"
    : gender === "Female" ? "linear-gradient(135deg,#be185d,#9d174d)"
    : "linear-gradient(135deg,#7c3aed,#6d28d9)";

  const fetchBookings = useCallback(async () => {
    setLoadingMessage(t("bookings.loading", "Fetching your bookings..."));
    setLoading(true);
    try {
      const all = await getPatientBookings(patientId);
      setBookings(all);
      setLastUpdated(Date.now());
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setBookings({ upcoming: [], past: [] });
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  useEffect(() => {
    if (patientId) fetchBookings();
  }, [patientId, fetchBookings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchBookings();
    setTimeout(() => setRefreshing(false), 700);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric"
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const isJoinable = (scheduledTime) => {
    if (!scheduledTime) return false;
    return (new Date(scheduledTime).getTime() - Date.now()) / 60000 <= 5;
  };

  const getUrgencyLabel = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const todayStr = new Date().toDateString();
    const tomorrowStr = new Date(Date.now() + 86400000).toDateString();
    if (d.toDateString() === todayStr) return "TODAY";
    if (d.toDateString() === tomorrowStr) return "TOMORROW";
    return null;
  };

  const formatLastUpdated = (ts) => {
    if (!ts) return null;
    void tick;
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 10) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    return `${Math.floor(diff / 3600)} hr ago`;
  };

  const groupByMonth = (list) => {
    const groups = {};
    list.forEach((b) => {
      const d = new Date(b.slot_time || b.scheduled_time || b.created_at || 0);
      const key = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    });
    return groups;
  };

  const chip = {
    paid:     isDark ? { bg:"#14532d", color:"#86efac" } : { bg:"#dcfce7", color:"#15803d" },
    unpaid:   isDark ? { bg:"#450a0a", color:"#fca5a5" } : { bg:"#fef2f2", color:"#dc2626" },
    walkin:   isDark ? { bg:"#164e63", color:"#67e8f9" } : { bg:"#ecfeff", color:"#0e7490" },
    appt:     isDark ? { bg:"#1e1b4b", color:"#a5b4fc" } : { bg:"#eef2ff", color:"#4338ca" },
    token:    isDark ? { bg:"#451a03", color:"#fcd34d", border:"#92400e" } : { bg:"#fffbeb", color:"#92400e", border:"#fde68a" },
    patId:    isDark ? { bg:"#1e3a5f", color:"#93c5fd" } : { bg:"#dbeafe", color:"#1d4ed8" },
    secUp:    isDark ? { bg:"#1e3a5f", color:"#93c5fd" } : { bg:"#eff6ff", color:"#2563eb" },
    secPast:  isDark ? { bg:"#334155", color:"#94a3b8" } : { bg:"#f3f4f6", color:"#9ca3af" },
    emptyUp:  isDark ? { bg:"#1e3a5f", border:"#1e40af" } : { bg:"#eff6ff", border:"#bfdbfe" },
    emptyPast:isDark ? { bg:"#334155", border:"#475569" } : { bg:"#f3f4f6", border:"#e5e7eb" },
  };

  /* ─── Booking Card ─── */
  const BookingCard = ({ b, isPast }) => {
    const isPaid = b.payment_status === "Paid";
    const isWalkin = b.booking_type === "W" || b.booking_type === "walkin";
    const isTele = b.isTelemedicine;
    const canJoin = isTele && b.patient_url && isJoinable(b.scheduled_time);
    const urgency = !isPast ? getUrgencyLabel(b.slot_time || b.scheduled_time) : null;
    const tokenNum = b.token_number
      ? (typeof b.token_number === "object" ? b.token_number?.number ?? "" : b.token_number)
      : null;

    const accentColor = isPast
      ? (isDark ? "#475569" : "#d1d5db")
      : urgency === "TODAY" ? "#f59e0b" : "#2563eb";

    return (
      <div
        className="booking-card-lift rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: isPast
            ? (isDark ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.55)")
            : (isDark ? "rgba(15,23,42,0.65)" : "rgba(255,255,255,0.82)"),
          border: "1px solid " + (isPast
            ? (isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.5)")
            : (isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.75)")),
          borderLeft: "4px solid " + accentColor,
          boxShadow: isPast
            ? (isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(15,40,120,0.06)")
            : (isDark
                ? "0 16px 40px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)"
                : "0 16px 40px rgba(15,40,120,0.12), 0 4px 12px rgba(15,40,120,0.06), inset 0 1px 0 rgba(255,255,255,0.95)"),
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          opacity: isPast ? 0.85 : 1,
        }}
      >
        <div className="px-4 pt-4 pb-4 flex flex-col gap-2.5">
          {/* Doctor + urgency pill */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-[15px] leading-snug truncate"
                 style={{ color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
                Dr. {b.doctor?.full_name || b.doctor?.name || "—"}
              </p>
              {b.doctor?.department && (
                <p className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>
                  {typeof b.doctor.department === "object"
                    ? b.doctor.department?.name || b.doctor.department?.department_name || ""
                    : b.doctor.department}
                </p>
              )}
            </div>
            {urgency && (
              <span
                className="flex-shrink-0 text-[10px] font-extrabold tracking-widest px-2 py-0.5 rounded-full"
                style={{
                  background: urgency === "TODAY"
                    ? (isDark ? "#422006" : "#fef3c7")
                    : (isDark ? "#052e16" : "#dcfce7"),
                  color: urgency === "TODAY"
                    ? (isDark ? "#fbbf24" : "#92400e")
                    : (isDark ? "#86efac" : "#15803d"),
                }}
              >
                {urgency === "TODAY" ? "⚡ Today" : "🌅 Tomorrow"}
              </span>
            )}
          </div>

          {/* Date row */}
          {b.slot_time && (
            <div className="flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}>
              <CalendarCheck size={13} style={{ color: accentColor, flexShrink: 0 }} />
              <span className="text-[12px] font-semibold">
                {formatDate(b.slot_time)} · {formatTime(b.slot_time)}
              </span>
            </div>
          )}

          {/* Chips row */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ background: (isPaid ? chip.paid : chip.unpaid).bg, color: (isPaid ? chip.paid : chip.unpaid).color }}
            >
              {isPaid ? `✓ ${t("bookings.paid")}` : t("bookings.notPaid")}
            </span>
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ background: (isWalkin ? chip.walkin : chip.appt).bg, color: (isWalkin ? chip.walkin : chip.appt).color }}
            >
              {isWalkin ? "Walk-in" : "Appointment"}
            </span>
            {tokenNum && (
              <button
                onClick={!isPast
                  ? () => setTokenModal({ token: tokenNum, doctorName: b.doctor?.full_name || b.doctor?.name || "" })
                  : undefined}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold transition-all active:scale-95"
                style={{
                  background: chip.token.bg,
                  color: chip.token.color,
                  border: `1px solid ${chip.token.border}`,
                  cursor: isPast ? "default" : "pointer",
                  touchAction: "manipulation",
                }}
              >
                🎫 {tokenNum}
              </button>
            )}
          </div>

          {/* Booking ID */}
          <p className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>
            #{b.id}
          </p>
        </div>

        {/* Telemedicine CTA */}
        {isTele && b.patient_url && (
          <div
            className="px-4 py-3 border-t"
            style={{ borderColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)" }}
          >
            {canJoin ? (
              <button
                onClick={() => window.open(b.patient_url, "_blank")}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white transition-all active:scale-95"
                style={{
                  background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
                  boxShadow: "0 4px 12px rgba(124,58,237,0.35)",
                  touchAction: "manipulation",
                  minHeight: 44,
                }}
              >
                <Video size={15} />
                {t("bookings.table.joinTelemedicine")}
              </button>
            ) : (
              <p className="text-center text-[11px] font-medium flex items-center justify-center gap-1.5"
                 style={{ color: isDark ? "#a78bfa" : "#a855f7" }}>
                <Video size={12} />
                {t("bookings.table.telemedicineAvailableSoon")}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ─── Empty State ─── */
  const EmptyState = ({ isUpcoming }) => {
    const box = isUpcoming ? chip.emptyUp : chip.emptyPast;
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
          style={{ background: box.bg, border: `1px solid ${box.border}` }}
        >
          <span style={{ fontSize: 28 }}>{isUpcoming ? "📅" : "🗂️"}</span>
        </div>
        <p className="font-bold text-sm mb-1" style={{ color: "var(--text-secondary)" }}>
          {isUpcoming ? t("bookings.noUpcoming") : t("bookings.noPast")}
        </p>
        {isUpcoming && (
          <>
            <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
              Book an appointment to see it here
            </p>
            {onSwitchToRegistration && (
              <button
                onClick={onSwitchToRegistration}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all active:scale-95"
                style={{
                  background: "linear-gradient(135deg,#2563eb,#0891b2)",
                  boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
                  touchAction: "manipulation",
                  minHeight: 44,
                }}
              >
                Book Now →
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  /* ─── Section ─── */
  const renderSection = (list, type) => {
    const isUpcoming = type === "upcoming";
    const countChip = isUpcoming ? chip.secUp : chip.secPast;
    const isPastCollapsed = !isUpcoming && !showPast;

    return (
      <div className="mb-6">
        {/* Section header */}
        <div
          className="flex items-center gap-2 mb-3"
          style={!isUpcoming ? { cursor: "pointer" } : {}}
          onClick={!isUpcoming ? () => setShowPast(v => !v) : undefined}
        >
          <div
            className="w-1 h-5 rounded-full flex-shrink-0"
            style={{
              background: isUpcoming
                ? "linear-gradient(180deg,#2563eb,#0891b2)"
                : isDark ? "#475569" : "#d1d5db",
            }}
          />
          <h4
            className="font-extrabold text-sm"
            style={{
              color: isUpcoming ? (isDark ? "#93c5fd" : "#1e3a8a") : "var(--text-secondary)",
              letterSpacing: "-0.01em",
            }}
          >
            {isUpcoming ? t("bookings.upcoming") : t("bookings.past")}
          </h4>
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-bold"
            style={{ background: countChip.bg, color: countChip.color }}
          >
            {list.length}
          </span>
          {!isUpcoming && (
            <span
              className="ml-auto text-[11px] font-semibold"
              style={{ color: isDark ? "#94a3b8" : "#9ca3af" }}
            >
              {isPastCollapsed ? "Show ▾" : "Hide ▴"}
            </span>
          )}
        </div>

        {isUpcoming && (
          list.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((b, i) => (
                <div key={b.id} className="slide-up-item" style={{ animationDelay: `${i * 0.07}s` }}>
                  <BookingCard b={b} isPast={false} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState isUpcoming={true} />
          )
        )}

        {!isUpcoming && !isPastCollapsed && (
          list.length > 0 ? (
            <div>
              {Object.entries(groupByMonth(list)).map(([month, items]) => (
                <div key={month} className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[10px] font-bold uppercase tracking-widest"
                      style={{ color: isDark ? "#64748b" : "#94a3b8" }}
                    >
                      {month}
                    </span>
                    <div className="flex-1 h-px" style={{ background: isDark ? "#334155" : "#e2e8f0" }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((b) => (
                      <BookingCard key={b.id} b={b} isPast={true} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState isUpcoming={false} />
          )
        )}

        {!isUpcoming && isPastCollapsed && list.length > 0 && (
          <p
            className="text-center text-[11px] py-2 cursor-pointer"
            style={{ color: "var(--text-muted)" }}
            onClick={() => setShowPast(true)}
          >
            {list.length} past booking{list.length !== 1 ? "s" : ""} — tap to show
          </p>
        )}
      </div>
    );
  };

  /* ─── Skeleton loader ─── */
  const SkeletonCard = () => (
    <div className="rounded-2xl overflow-hidden p-4 flex flex-col gap-3"
         style={{ background: "var(--surface)", border: "1px solid var(--border)", borderLeft: "4px solid var(--border)" }}>
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-2 flex-1">
          <div className="skeleton h-4 w-3/4" />
          <div className="skeleton h-3 w-1/2" />
        </div>
      </div>
      <div className="skeleton h-3 w-40" />
      <div className="flex gap-2">
        <div className="skeleton h-5 w-16 rounded-full" />
        <div className="skeleton h-5 w-20 rounded-full" />
      </div>
      <div className="skeleton h-3 w-24" />
    </div>
  );

  /* ─── Main render ─── */
  return (
    <div className="relative">
      {loading && !bookings.upcoming.length && !bookings.past.length && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {[1,2,3].map(i => <SkeletonCard key={i} />)}
        </div>
      )}
      {loading && (bookings.upcoming.length > 0 || bookings.past.length > 0) && <Loader message={loadingMessage} />}

      {/* Patient identity bar */}
      <div className="identity-bar flex items-center justify-between mb-5 p-3 rounded-2xl">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="avatar-ring w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-white text-sm flex-shrink-0"
            style={{ background: avatarBg }}
          >
            {initials}
          </div>
          <div className="min-w-0">
            {patientName && (
              <p className="font-extrabold text-sm leading-snug truncate"
                 style={{ color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
                {patientName}
              </p>
            )}
            {patientIdentifier && (
              <span
                className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-full inline-block mt-0.5"
                style={{ background: chip.patId.bg, color: chip.patId.color }}
              >
                #{patientIdentifier}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <button
            onClick={handleRefresh}
            title="Refresh"
            className="flex items-center justify-center w-9 h-9 rounded-xl transition-all active:scale-95"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              touchAction: "manipulation",
            }}
          >
            <RefreshCcw
              size={15}
              style={{ color: "#2563eb" }}
              className={refreshing ? "animate-spin" : ""}
            />
          </button>
          {lastUpdated && (
            <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
              {formatLastUpdated(lastUpdated)}
            </span>
          )}
        </div>
      </div>

      {renderSection(bookings.upcoming, "upcoming")}
      {renderSection(bookings.past, "past")}

      {/* Token modal */}
      {tokenModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setTokenModal(null)}
        >
          <div
            className="w-full max-w-xs rounded-3xl p-6 flex flex-col items-center gap-4 animate-fadeIn"
            style={{
              background: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.88)",
              border: "1px solid " + (isDark ? "rgba(251,191,36,0.2)" : "rgba(251,191,36,0.5)"),
              boxShadow: isDark
                ? "0 32px 64px rgba(0,0,0,0.5), 0 8px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)"
                : "0 32px 64px rgba(15,40,120,0.18), 0 8px 24px rgba(15,40,120,0.1), inset 0 1px 0 rgba(255,255,255,1)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="token-ring-pulse w-20 h-20 rounded-2xl flex items-center justify-center text-4xl"
              style={{
                background: isDark
                  ? "linear-gradient(135deg,#451a03,#78350f)"
                  : "linear-gradient(135deg,#fef3c7,#fde68a)",
                border: "2px solid " + (isDark ? "#92400e" : "#fcd34d"),
              }}
            >
              🎫
            </div>
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-widest mb-1"
                 style={{ color: "var(--text-muted)" }}>
                Your Token Number
              </p>
              <p
                className="text-6xl font-black"
                style={{ color: isDark ? "#fcd34d" : "#78350f", letterSpacing: "-0.03em" }}
              >
                {tokenModal.token}
              </p>
              {tokenModal.doctorName && (
                <p className="text-sm mt-2 font-medium" style={{ color: "var(--text-secondary)" }}>
                  Dr. {tokenModal.doctorName}
                </p>
              )}
            </div>
            <p className="text-[11px] text-center" style={{ color: "var(--text-muted)" }}>
              Show this number at the reception
            </p>
            <button
              onClick={() => setTokenModal(null)}
              className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all active:scale-95"
              style={{
                background: "linear-gradient(135deg,#2563eb,#0891b2)",
                boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
                touchAction: "manipulation",
                minHeight: 44,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
